import PropTypes from "prop-types";
import { useMemo, Fragment, useRef, useState, useEffect } from "react";

// Material-UI
import {
  Box,
  NativeSelect,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Icons
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";

// React Table
import {
  useExpanded,
  useFilters,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

// Project Components & Utils
import { HeaderSort, TablePagination } from "components/third-party/ReactTable";
import { renderFilterTypes, GlobalFilter, DateColumnFilter } from "utils/react-table";
import { updateCallStat } from "store/reducers/data";
import { useSelector } from "store";

// --- Performance Stat Component ---
const PerformanceStat = ({ answered, notAnswered, goal = 30 }) => {
  const total = answered + notAnswered;
  const answerPercentage = total > 0 ? Math.round((answered / total) * 100) : 0;
  const goalProgress = Math.min(Math.round((answered / goal) * 100), 100);

  return (
    <Stack direction="row" alignItems="center" spacing={3} sx={{ ml: 2, mr: 1 }}>
      {/* Answer vs No Answer Ratio Bar */}
      <Stack direction="column" alignItems="flex-start" spacing={0} sx={{ minWidth: 100 }}>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            color: "text.secondary",
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          Answer Ratio ({answerPercentage}%)
        </Typography>
        <Box
          sx={{
            width: "100%",
            mt: 0.5,
            bgcolor: "error.main",
            borderRadius: 1,
            height: 6,
            overflow: "hidden",
            display: "flex",
          }}
        >
          <Box
            sx={{
              width: `${answerPercentage}%`,
              bgcolor: "success.main",
              height: "100%",
              transition: "width 0.5s ease-in-out",
            }}
          />
        </Box>
      </Stack>

      {/* Daily Goal Progress Bar */}
      <Stack direction="column" alignItems="flex-start" spacing={0} sx={{ minWidth: 100 }}>
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            color: "text.secondary",
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          Daily Goal ({answered}/{goal})
        </Typography>
        <Box
          sx={{
            width: "100%",
            mt: 0.5,
            bgcolor: "divider",
            borderRadius: 1,
            height: 6,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${goalProgress}%`,
              bgcolor: goalProgress === 100 ? "success.main" : "primary.main",
              height: "100%",
              transition: "width 0.5s ease-in-out",
            }}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

// --- StatCounter Component ---
const StatCounter = ({ label, value, setter, color, type, currentUser }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      px: 1.5,
      py: 0.5,
      bgcolor: "background.paper",
    }}
  >
    <Typography
      sx={{
        fontSize: "0.75rem",
        fontWeight: 700,
        color: "text.secondary",
        textTransform: "uppercase",
        mr: 1,
      }}
    >
      {label}: {value}
    </Typography>

    <IconButton
      size="small"
      onClick={() => {
        if (value > 0) {
          setter((prev) => prev - 1);
          if (currentUser?.id) {
            updateCallStat(currentUser.id, type, -1);
          }
        }
      }}
      sx={{ p: 0.5 }}
    >
      <MinusOutlined style={{ fontSize: "10px" }} />
    </IconButton>

    <IconButton
      size="small"
      color={color}
      onClick={() => {
        setter((prev) => prev + 1);
        if (currentUser?.id) {
          updateCallStat(currentUser.id, type, 1);
        }
      }}
      sx={{ p: 0.5 }}
    >
      <PlusOutlined style={{ fontSize: "10px" }} />
    </IconButton>
  </Stack>
);

// --- Main Table Component ---
export function ColdListTable({ columns, data, setLimit, currentUser }) {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("sm"));
  const { outreachData } = useSelector((state) => state.data);

  const [answered, setAnswered] = useState(0);
  const [notAnswered, setNotAnswered] = useState(0);

  // Sync state from Firestore
  useEffect(() => {
    if (outreachData && outreachData.length > 0 && currentUser?.id) {
      const today = new Date().toISOString().split("T")[0];
      const monthDoc = outreachData[0];
      const todayData = monthDoc[today] || {};
      const userSuffix = currentUser.id === "wDwxNdcPgYempbC4aDXciSDJ82f2" ? "eeli" : "joose";

      setAnswered(todayData[`answered_${userSuffix}`] || 0);
      setNotAnswered(todayData[`no_answer_${userSuffix}`] || 0);
    }
  }, [outreachData, currentUser]);

  const defaultColumn = useMemo(() => ({ Filter: DateColumnFilter }), []);
  const filterTypes = useMemo(() => renderFilterTypes, []);

  const tableColumns = useMemo(
    () =>
      columns.map((col) => ({
        ...col,
        incrementAnswered: () => {
          if (currentUser?.id) updateCallStat(currentUser.id, "answered", 1);
        },
        incrementNoAnswer: () => {
          if (currentUser?.id) updateCallStat(currentUser.id, "no_answer", 1);
        },
      })),
    [columns, currentUser]
  );

  const initialState = useMemo(
    () => ({
      hiddenColumns: ["id", "business_id", "website", "rating", "userRatingCount"],
      pageIndex: 0,
      pageSize: 10,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    gotoPage,
    setPageSize,
    state: { globalFilter, pageIndex, pageSize },
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns: tableColumns,
      data,
      filterTypes,
      defaultColumn,
      initialState,
      autoResetPage: false,
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const componentRef = useRef(null);
  const selectSX = { minWidth: 40, fontSize: "0.8rem" };

  return (
    <>
      <Stack
        direction={matchDownSM ? "column" : "row"}
        spacing={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{ p: 2, pb: 2 }}
      >
        <Stack direction="row" spacing={2}>
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            size="small"
          />
        </Stack>
      </Stack>

      <Box ref={componentRef}>
        <Table {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup, i) => (
              <TableRow key={i} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, x) => (
                  <TableCell
                    key={x}
                    {...column.getHeaderProps([{ className: column.className }])}
                    style={{
                      display:
                        matchDownSM && column.className === "hide-on-mobile" ? "none" : undefined,
                    }}
                    sx={{
                      minWidth: column.minWidth,
                      fontSize: "0.8rem",
                      textTransform: "capitalize",
                    }}
                  >
                    <HeaderSort column={column} sort />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <Fragment key={i}>
                  <TableRow {...row.getRowProps()} sx={{ cursor: "pointer" }}>
                    {row.cells.map((cell, j) => (
                      <TableCell
                        key={j}
                        {...cell.getCellProps([{ className: cell.column.className }])}
                        style={{
                          display:
                            matchDownSM && cell.column.className === "hide-on-mobile"
                              ? "none"
                              : undefined,
                        }}
                        sx={{ p: 1, m: 0, fontSize: "0.8rem" }}
                      >
                        {cell.render("Cell")}
                      </TableCell>
                    ))}
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ p: 2 }}>
        <TablePagination
          gotoPage={gotoPage}
          rows={rows}
          setPageSize={setPageSize}
          pageSize={pageSize}
          pageIndex={pageIndex}
        />
      </Box>
    </>
  );
}

ColdListTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  setLimit: PropTypes.func,
  currentUser: PropTypes.object,
};
